import { FHWAClass, getFHWAClassDisplayName, getTargetTypeDisplayName, Lane, TargetSource, TargetType, TrackedTarget, TrackedTargetHistory, TurnType } from "@app/shared";
import cn from "classnames";
import { useCallback, useEffect, useState } from "react";
import { EnumSelect, IconLink, Icons } from "../../../shared";
import { getTrackedTargetHistory } from "../TargetHelpers";

type UserTargetProps = {
    target: TrackedTarget;
    currentFrame: { radar: number, video: number };
    onClick?: (target: TrackedTarget) => void;
    onChanged?: (newTarget: TrackedTarget) => void;
    onDelete?: (target: TrackedTarget) => void;
    setNeedsVideoClick?: (needsClick: boolean) => void;
    needsVideoClick?: boolean;
    lastVideoClick?: { x: number; y: number };
};
const precision = 3;

export const UserTarget: React.FC<UserTargetProps> = ({ target, currentFrame, onChanged, onDelete, onClick, ...props }) => {
    const [isInFrame, setIsInFrame] = useState(false);
    const [currentTrackHistory, setCurrentTrackHistory] = useState<TrackedTargetHistory>();

    useEffect(() => {
        //Is this frame in the range of the target?
        if ((target.source === TargetSource.Camera && (target.lastFrameTracked < currentFrame.video || target.firstFrameTracked > currentFrame.video)) ||
            (target.source === TargetSource.Radar && (target.lastFrameTracked < currentFrame.radar || target.firstFrameTracked > currentFrame.radar))) {
            if (isInFrame) {
                setIsInFrame(false);
            }
            return;
        }
        //We are in the range of the target frames
        if (!isInFrame) {
            setIsInFrame(true);
        }
    }, [target, currentFrame]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        //Get the current frame details
        if (!isInFrame && currentTrackHistory) {
            setCurrentTrackHistory(undefined);
        }

        if (isInFrame) {
            var newTrackHistory = getTrackedTargetHistory(target, currentFrame);
            setCurrentTrackHistory(newTrackHistory)
        }

    }, [isInFrame, currentFrame, target, setCurrentTrackHistory]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOnClick = useCallback(() => {
        onClick?.(target);
    }, [onClick, target]);

    const handleSetStartFrameOnClick: React.MouseEventHandler<HTMLDivElement> = useCallback((e) => {
        onChanged?.({ ...target, firstFrameTracked: target.source === TargetSource.Radar ? currentFrame.radar : currentFrame.video });
        e.stopPropagation();
    }, [target, currentFrame, onChanged]);

    const handleSetEndFrameOnClick: React.MouseEventHandler<HTMLDivElement> = useCallback((e) => {
        onChanged?.({ ...target, lastFrameTracked: target.source === TargetSource.Radar ? currentFrame.radar : currentFrame.video });
        e.stopPropagation();
    }, [target, currentFrame, onChanged]);

    const handleDeleteOnClick: React.MouseEventHandler<HTMLDivElement> = useCallback((e) => {
        onDelete?.(target);
        e.stopPropagation();
    }, [target, onDelete]);

    const handleClearHistoryOnClick: React.MouseEventHandler<HTMLDivElement> = useCallback((e) => {
        onChanged?.({ ...target, targetHistory: [] });
        e.stopPropagation();
    }, [target, onChanged]);

    return (
        <div onClick={handleOnClick}
            key={target.radarId}
            className={cn("border-2 p-0 my-1 rounded text-sm",
                (isInFrame ? "border-bright-blue" : "border-separators-page"),
                { "cursor-pointer hover:border-light-blue": (onClick && !currentTrackHistory) })}
            style={{ height: 170 }} >

            <div className="font-bold bg-background-page-highlight-gray p-2 flex flex-row">
                <div className="flex-grow">
                    <div className="flex flex-row items-center">
                        <div>Target {target.radarId}: </div>
                        <EnumSelect
                            className="inline h-5 text-xs p-0"
                            enumType={TargetType}
                            nameToDisplayConverter={getTargetTypeDisplayName}
                            value={target.reportedType}
                            onChange={(e) => onChanged?.({ ...target, reportedType: e as TargetType })}
                        />
                        <div>  (FHWA: </div>
                        <EnumSelect
                            className="inline h-5 text-xs p-0"
                            enumType={FHWAClass}
                            nameToDisplayConverter={getFHWAClassDisplayName}
                            value={target.fhwaType ?? 15}
                            onChange={(e) => onChanged?.({ ...target, fhwaType: e as FHWAClass })}
                        />
                        <div>)</div>
                    </div>
                    <div className="text-xs font-regular font-semibold font-sm">{target.firstFrameTracked} -&gt; {target.lastFrameTracked}</div>
                </div>
                <div className="flex gap-3">
                    <IconLink Icon={Icons.ListRemove} onClick={handleClearHistoryOnClick} iconSize={26} />
                    <IconLink Icon={Icons.ArrowRightFromBracket} onClick={handleSetStartFrameOnClick} iconSize={20} />
                    <IconLink Icon={Icons.ArrowRightToBracket} onClick={handleSetEndFrameOnClick} iconSize={20} />
                    <IconLink Icon={Icons.Trash} onClick={handleDeleteOnClick} iconSize={20} />
                </div>
            </div>

            <div className="px-2 py-1">
                <div className="flex flex-row gap-3">
                    <div className="flex-grow">
                        <div className="font-semibold">Target Details:</div>
                        <div className="flex flex-row items-center">
                            <div>Lane:</div>
                            <EnumSelect
                                className="inline h-5 text-xs p-0"
                                emptyOptionDisabled={false}
                                emptyOptionText="None"
                                enumType={Lane}
                                value={target.laneNumber}
                                onChange={(e) => onChanged?.({ ...target, laneNumber: e as Lane })}
                            />
                        </div>
                        <div className="flex flex-row items-center">
                            <div>Turn:</div>
                            <EnumSelect
                                className="inline h-5 text-xs p-0"
                                emptyOptionDisabled={false}
                                emptyOptionText="None"
                                enumType={TurnType}
                                value={target.turn}
                                onChange={(e) => onChanged?.({ ...target, turn: e as TurnType })}
                            />

                        </div>
                        <div>Events: {(target.events?.length ?? 0) > 0 ? target.events!.length + " Events" : "0"}</div>
                    </div>
                    <div className="flex-grow">
                        {isInFrame && <>
                            <div className="font-semibold">{target.source === TargetSource.Radar ? "Radar" : "Video"} Frame {target.source === TargetSource.Radar ? currentFrame.radar : currentFrame.video} ({currentTrackHistory?.pointCount ?? 0} points)</div>
                            <div>Position: ({currentTrackHistory?.position.x.toFixed(precision) ?? ""},
                                {currentTrackHistory?.position.y.toFixed(precision) ?? ""},
                                {currentTrackHistory?.position.z.toFixed(precision) ?? ""})
                                {!props.needsVideoClick &&
                                    <IconLink Icon={Icons.Target} onClick={(e) => { props.setNeedsVideoClick?.(true); e.stopPropagation(); }} iconSize={16} />
                                }
                            </div>
                            <div>Velocity: ({currentTrackHistory?.velocity.x.toFixed(precision) ?? ""},
                                {currentTrackHistory?.velocity.y.toFixed(precision) ?? ""},
                                {currentTrackHistory?.velocity.z.toFixed(precision) ?? ""})
                            </div>
                            <div>Acceleration: ({currentTrackHistory?.acceleration.x.toFixed(precision) ?? ""},
                                {currentTrackHistory?.acceleration.y.toFixed(precision) ?? ""},
                                {currentTrackHistory?.acceleration.z.toFixed(precision) ?? ""})
                            </div>
                        </>}
                    </div>
                </div>
            </div>
        </div >
    );
}